import { EmissionFieldConfig, EmissionFieldVisibility } from '../services/organisation/organisation.model';
import { ICustomField } from '../services/custom-field/custom-field.model';

const DEFAULT_VISIBILITY: EmissionFieldVisibility = 'optional';

function getVisibility(config: EmissionFieldConfig | null | undefined, key: keyof EmissionFieldConfig): EmissionFieldVisibility {
  return config?.[key] ?? DEFAULT_VISIBILITY;
}

export interface EmissionFieldFlags {
  hideCountry: boolean;
  hideLocation: boolean;
  hideSupplier: boolean;
  hideVehicle: boolean;
  hideProduct: boolean;
  hideProject: boolean;
  hideEts: boolean;
  hideExcludeFromReporting: boolean;
  mandatoryFields: string[];
  visibleCustomFields: ICustomField[];
  mandatoryCustomFieldIds: string[];
}

/**
 * Derives hide/mandatory flags for EmissionsInputAdvanced from the org's emissionFieldConfig
 * and the custom fields' displayMode values.
 */
export function resolveEmissionFieldFlags(
  config: EmissionFieldConfig | null | undefined,
  customFields: ICustomField[],
): EmissionFieldFlags {
  const getV = (key: keyof EmissionFieldConfig) => getVisibility(config, key);

  const mandatory: string[] = [];
  const addIfMandatory = (key: string, configKey: keyof EmissionFieldConfig) => {
    if (getV(configKey) === 'mandatory') mandatory.push(key);
  };

  addIfMandatory('country', 'country');
  addIfMandatory('location', 'location');
  addIfMandatory('supplier', 'supplier');
  addIfMandatory('vehicle', 'vehicle');
  addIfMandatory('product', 'product');
  addIfMandatory('project', 'project');
  addIfMandatory('ets', 'ets');
  addIfMandatory('excludeFromReporting', 'excludeFromReporting');

  // Custom fields: mandatory ones add their id to mandatoryFields
  const visibleCustomFields = customFields.filter(cf => (cf.displayMode ?? 'optional') !== 'hidden');
  const mandatoryCustomFieldIds = customFields
    .filter(cf => (cf.displayMode ?? 'optional') === 'mandatory')
    .map(cf => cf.id);

  mandatory.push(...mandatoryCustomFieldIds);

  return {
    hideCountry: getV('country') === 'hidden',
    hideLocation: getV('location') === 'hidden',
    hideSupplier: getV('supplier') === 'hidden',
    hideVehicle: getV('vehicle') === 'hidden',
    hideProduct: getV('product') === 'hidden',
    hideProject: getV('project') === 'hidden',
    hideEts: getV('ets') === 'hidden',
    hideExcludeFromReporting: getV('excludeFromReporting') === 'hidden',
    mandatoryFields: mandatory,
    visibleCustomFields,
    mandatoryCustomFieldIds,
  };
}
